/**
 * CalendarWeekView Component
 * Displays appointments in a weekly calendar view with improved time blocks
 */

interface CalendarWeekViewProps {
  selectedDate: Date;
  appointments: any[];
  onAppointmentClick: (appointment: any) => void;
  onTimeSlotClick: (date: Date, hour: number) => void;
}

export function CalendarWeekView({
  selectedDate,
  appointments,
  onAppointmentClick,
  onTimeSlotClick,
}: CalendarWeekViewProps) {
  const getWeekDays = () => {
    const start = new Date(selectedDate);
    const day = start.getDay();
    start.setDate(start.getDate() - day);

    const days: Date[] = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      days.push(date);
    }
    return days;
  };

  const getAppointmentsForDate = (date: Date) => {
    return appointments.filter((apt) => {
      const aptDate = new Date(apt.startTime);
      return (
        aptDate.getDate() === date.getDate() &&
        aptDate.getMonth() === date.getMonth() &&
        aptDate.getFullYear() === date.getFullYear()
      );
    }).sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
  };

  // Detect overlapping appointments and assign columns
  const getAppointmentLayout = (dayAppointments: any[]) => {
    const layout: { appointment: any; column: number; totalColumns: number }[] = [];
    
    dayAppointments.forEach((apt) => {
      const aptStart = new Date(apt.startTime).getTime();
      const aptEnd = new Date(apt.endTime).getTime();
      
      // Find overlapping appointments that are already placed
      const overlapping = layout.filter((item) => {
        const itemStart = new Date(item.appointment.startTime).getTime();
        const itemEnd = new Date(item.appointment.endTime).getTime();
        return aptStart < itemEnd && aptEnd > itemStart;
      });
      
      // Find the first available column
      let column = 0;
      const usedColumns = overlapping.map(item => item.column);
      while (usedColumns.includes(column)) {
        column++;
      }
      
      layout.push({ appointment: apt, column, totalColumns: 1 });
    });
    
    // Update totalColumns for overlapping groups
    layout.forEach((item) => {
      const aptStart = new Date(item.appointment.startTime).getTime();
      const aptEnd = new Date(item.appointment.endTime).getTime();
      
      const overlapping = layout.filter((other) => {
        const otherStart = new Date(other.appointment.startTime).getTime();
        const otherEnd = new Date(other.appointment.endTime).getTime();
        return aptStart < otherEnd && aptEnd > otherStart;
      });
      
      item.totalColumns = Math.max(...overlapping.map(o => o.column + 1));
    });
    
    return layout;
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  // Dynamically determine time range based on appointments
  // Default to business hours (8 AM - 6 PM), but expand if appointments exist outside this range
  const getTimeSlots = () => {
    let startHour = 8;
    let endHour = 18;

    // Check if any appointments fall outside business hours
    if (appointments.length > 0) {
      appointments.forEach((apt) => {
        const aptStartHour = new Date(apt.startTime).getHours();
        const aptEndHour = new Date(apt.endTime).getHours();
        
        if (aptStartHour < startHour) {
          startHour = aptStartHour;
        }
        if (aptEndHour > endHour) {
          endHour = aptEndHour;
        }
      });
    }

    const slots: { hour: number; label: string; isBusinessHours: boolean }[] = [];
    for (let hour = startHour; hour <= endHour; hour++) {
      const time = new Date();
      time.setHours(hour, 0, 0, 0);
      slots.push({
        hour,
        label: time.toLocaleTimeString('en-US', {
          hour: 'numeric',
          hour12: true,
        }),
        isBusinessHours: hour >= 8 && hour <= 18,
      });
    }
    return slots;
  };

  const weekDays = getWeekDays();
  const timeSlots = getTimeSlots();

  return (
    <div className="overflow-x-auto -mx-2 sm:mx-0">
      <div className="min-w-[600px] sm:min-w-[800px]">
        {/* Header with days - more compact */}
        <div className="grid grid-cols-8 border-b border-gray-300 bg-gray-50 sticky top-0 z-10">
          <div className="p-1.5 sm:p-2 text-[10px] sm:text-xs font-semibold text-gray-600 border-r border-gray-200">Time</div>
          {weekDays.map((date) => {
            const today = isToday(date);
            return (
              <div
                key={date.toISOString()}
                className={`p-1.5 sm:p-2 text-center border-r border-gray-200 last:border-r-0 ${
                  today ? 'bg-red-50' : ''
                }`}
              >
                <div className="text-[9px] sm:text-[10px] font-medium text-gray-600 uppercase">
                  {date.toLocaleDateString('en-US', { weekday: 'short' })}
                </div>
                <div
                  className={`text-base sm:text-lg font-bold mt-0.5 ${
                    today
                      ? 'text-[#C41E3A]'
                      : 'text-gray-900'
                  }`}
                >
                  {date.getDate()}
                </div>
                <div className="text-[9px] sm:text-[10px] text-gray-500">
                  {date.toLocaleDateString('en-US', { month: 'short' })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Time grid - more compact */}
        <div className="relative">
          {timeSlots.map((slot) => (
            <div key={slot.hour} className="grid grid-cols-8 border-b border-gray-200">
              <div className={`p-1.5 sm:p-2 text-[10px] sm:text-xs font-medium text-gray-600 text-right pr-2 sm:pr-3 border-r border-gray-200 ${
                slot.isBusinessHours ? 'bg-gray-50' : 'bg-amber-50'
              }`}>
                {slot.label}
                {!slot.isBusinessHours && (
                  <span className="block text-[8px] sm:text-[10px] text-amber-600">After</span>
                )}
              </div>
              {weekDays.map((date) => {
                const dayAppointments = getAppointmentsForDate(date);
                const appointmentLayout = getAppointmentLayout(dayAppointments);
                const hourAppointments = appointmentLayout.filter((item) => {
                  const aptHour = new Date(item.appointment.startTime).getHours();
                  return aptHour === slot.hour;
                });

                const today = isToday(date);

                return (
                  <div
                    key={`${date.toISOString()}-${slot.hour}`}
                    className={`min-h-[40px] sm:min-h-[50px] p-1 sm:p-1.5 transition-colors relative border-r border-gray-100 last:border-r-0 group ${
                      today ? 'bg-red-50/30' : slot.isBusinessHours ? '' : 'bg-amber-50/30'
                    }`}
                  >
                    {hourAppointments.length === 0 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onTimeSlotClick(date, slot.hour);
                        }}
                        className="absolute top-0.5 right-0.5 w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-[#C41E3A] text-white flex items-center justify-center hover:bg-[#8B0000] transition-colors shadow-sm opacity-0 group-hover:opacity-100"
                        title="Create appointment"
                      >
                        <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                      </button>
                    )}
                    {hourAppointments.map((item) => {
                      const apt = item.appointment;
                      const startTime = new Date(apt.startTime);
                      const endTime = new Date(apt.endTime);
                      const duration =
                        (endTime.getTime() - startTime.getTime()) / (1000 * 60);
                      const height = Math.max((duration / 60) * 40, 32);
                      
                      // Calculate width and position based on columns
                      const widthPercent = 100 / item.totalColumns;
                      const leftPercent = (item.column * widthPercent);

                      return (
                        <div
                          key={apt.id}
                          className="absolute bg-[#C41E3A] text-white rounded-lg px-1.5 sm:px-2 py-1 sm:py-1.5 text-[10px] sm:text-xs cursor-pointer hover:bg-[#8B0000] transition-colors overflow-hidden shadow-md"
                          style={{ 
                            height: `${height}px`,
                            width: `calc(${widthPercent}% - 3px)`,
                            left: `calc(${leftPercent}% + 1.5px)`,
                          }}
                          onClick={() => onAppointmentClick(apt)}
                          title={`${apt.title}\n${startTime.toLocaleTimeString('en-US', {
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true,
                          })} - ${endTime.toLocaleTimeString('en-US', {
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true,
                          })}`}
                        >
                          <div className="font-semibold truncate text-[9px] sm:text-[10px]">{apt.title}</div>
                          <div className="text-[8px] sm:text-[10px] opacity-90 mt-0.5">
                            {startTime.toLocaleTimeString('en-US', {
                              hour: 'numeric',
                              minute: '2-digit',
                              hour12: true,
                            })}
                          </div>
                          {apt.location && item.totalColumns === 1 && (
                            <div className="text-[8px] sm:text-[10px] opacity-75 truncate mt-0.5 hidden sm:block">
                              📍 {apt.location}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
